/**
 * Shared date utilities for @fm/ui and @fm/web-components packages.
 *
 * These handle CALENDAR DATES, not instants. "September 6" is September 6 for
 * a user in London, Phoenix or Tokyo, exactly like a birthday — it has no time
 * and belongs to no timezone. So nothing here converts through one.
 *
 * They used to format through a hardcoded `America/New_York`, on the reasoning
 * that a fixed zone gives deterministic output. It does — deterministically in
 * New York. Everywhere else it turned a calendar date into an instant and read
 * it back on the wrong side of midnight: a London user blocking Sep 6-7 saved
 * Sep 5-6.
 *
 * The pitfall the old comment named is real, but it lives in `new Date("...")`
 * parsing a bare date as UTC midnight. `parseLocalDate` is what avoids it, and
 * it needs no timezone to do so.
 */

import { format } from "date-fns";

/**
 * Parse a date string (YYYY-MM-DD) or Date object into a Date that represents
 * local midnight, not UTC midnight.
 *
 * Why this matters:
 *   new Date("2026-02-19")          → 2026-02-19T00:00:00Z (UTC)
 *   new Date(2026, 1, 19)           → 2026-02-19T00:00:00 local
 *
 * The first form displays as Feb 18 in US timezones. The second form always
 * displays as Feb 19 regardless of the browser's timezone.
 */
export function parseLocalDate(value: Date | string): Date {
  if (value instanceof Date) return value;

  const s = String(value);

  // Already has a time component (ISO-8601 with T) — leave it alone.
  if (s.includes("T")) return new Date(s);

  const parts = s.split("-");
  const year = parseInt(parts[0]!, 10);
  const month = parseInt(parts[1]!, 10);
  const day = parseInt(parts[2]!, 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error(`Invalid date format: "${s}". Expected YYYY-MM-DD.`);
  }

  return new Date(year, month - 1, day);
}

/** "Thu, Feb 19, 2026". */
export function formatDate(value: Date | string): string {
  try {
    return format(parseLocalDate(value), "EEE, MMM d, yyyy");
  } catch {
    return String(value);
  }
}

/** "Feb 19". */
export function formatDateShort(value: Date | string): string {
  try {
    return format(parseLocalDate(value), "MMM d");
  } catch {
    return String(value);
  }
}

/** "Feb 19, 2026". */
export function formatDateMedium(value: Date | string): string {
  try {
    return format(parseLocalDate(value), "MMM d, yyyy");
  } catch {
    return String(value);
  }
}

/** "February 19, 2026". */
export function formatDateLong(value: Date | string): string {
  try {
    return format(parseLocalDate(value), "MMMM d, yyyy");
  } catch {
    return String(value);
  }
}

/** "February 2026" (month + year only). */
export function formatMonthYear(value: Date | string): string {
  try {
    return format(parseLocalDate(value), "MMMM yyyy");
  } catch {
    return String(value);
  }
}

/**
 * Convert a Date back to "YYYY-MM-DD" using its own calendar day.
 *
 * This is the only serializer that may produce a date for the API. Never use
 * `.toISOString()` for that: it renders the instant in UTC, so a local-midnight
 * Date east of Greenwich reports the previous day.
 */
export function toDateString(value: Date | string): string {
  try {
    return format(parseLocalDate(value), "yyyy-MM-dd");
  } catch {
    return String(value);
  }
}

/** Number of calendar days between two dates (end − start). */
export function daysBetween(start: Date | string, end: Date | string): number {
  const s = parseLocalDate(start);
  const e = parseLocalDate(end);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / 86_400_000));
}

/**
 * Number of overnight stays between a check-in and a check-out.
 *
 * Sep 6 → Sep 8 is two nights. This used to subtract one, which is the right
 * formula for `start_date`/`end_date` — the first and LAST day of usage — but
 * every caller passes `pick_up`/`return`, so it lost a night and reported
 * "1 nights" on a two-night rental.
 */
export function nightsBetween(
  start: Date | string,
  end: Date | string,
): number {
  return daysBetween(start, end);
}
